import { DocusignConfig } from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { ENVIRONMENT } from '@monetize/ui/config';
import { useCallback, useEffect, useRef, useState } from 'react';
import { EsignUpdateParams } from '../../../../../api/settingsService';
import { DOCUSIGN_ACCOUNT_URL_TO_OAUTH_URL_MAPPING } from '../../../../../constants/esign';
import { SETTINGS_DOCUSIGN_CALLBACK } from '../../../../../constants/routes';

let windowRef: Maybe<Window>;
let callbackFn: Maybe<(success: boolean) => void>;

/**
 * Message handler for the child window to tell us when the oauth flow is complete
 * @param event
 */
function handleWindowEvent(event: MessageEvent) {
  try {
    if (event.data?.status === 'success') {
      if (windowRef) {
        windowRef.close();
        window.removeEventListener('message', handleWindowEvent);
      }
      callbackFn?.(true);
    } else if (event.data?.status === 'error') {
      if (windowRef) {
        window.removeEventListener('message', handleWindowEvent);
      }
      callbackFn?.(false);
    }
  } catch (ex) {
    callbackFn?.(false);
  }
}

/**
 * Docusign requires user consent, which is achieved by a partial OAuth flow (just the first step)
 * the callback has a code, but since it is not a full OAuth flow, that part is just ignored
 *
 * Docs: https://www.docusign.com/blog/developers/oauth-jwt-granting-consent
 */
export const useEsignConsentHandler = ({
  onConsent,
}: {
  onConsent: (
    success: boolean,
    updateParams: EsignUpdateParams,
  ) => Promise<void>;
}) => {
  const [isAwaitingConsent, setIsAwaitingConsent] = useState(false);
  const onConsentRef = useRef(onConsent);
  onConsentRef.current = onConsent;

  // Cleanup listeners and close the page on unmount
  useEffect(() => {
    return () => {
      windowRef?.close();
      window.removeEventListener('message', handleWindowEvent);
      windowRef = undefined;
      callbackFn = undefined;
    };
  }, []);

  const handleConsent = useCallback((updateParams: EsignUpdateParams) => {
    setIsAwaitingConsent(true);

    // if the user takes too long, remove loading state in case they need to start over
    // the user can still finish the process even after this happens
    const timeout = setTimeout(() => {
      setIsAwaitingConsent(false);
    }, 30_000);

    // Handle notification from consent window, let parent know to try the save operation again
    callbackFn = (success) => {
      onConsentRef.current(success, updateParams);
      setIsAwaitingConsent(false);
      clearTimeout(timeout);
    };

    // Open a new window for the consent flow
    window.removeEventListener('message', handleWindowEvent);
    const strWindowFeatures = 'toolbar=no, menubar=no, width=1025, height=700';
    const url = new URL(
      DOCUSIGN_ACCOUNT_URL_TO_OAUTH_URL_MAPPING[
        (updateParams.data as DocusignConfig).accountBaseURI
      ],
    );
    url.searchParams.set('scope', 'signature impersonation');
    url.searchParams.set('client_id', ENVIRONMENT.DOCUSIGN_CLIENT_ID);
    url.searchParams.set(
      'redirect_uri',
      `${window.location.origin}${SETTINGS_DOCUSIGN_CALLBACK}`,
    );
    windowRef = window.open(url, 'Consent with Docusign', strWindowFeatures);
    window.addEventListener('message', handleWindowEvent, false);
  }, []);

  return {
    handleConsent,
    isAwaitingConsent,
  };
};
